/**
 * Frontend OpenTelemetry wiring — LOCAL DEV ONLY.
 *
 * Mirrors the backend: the .NET services only export OTel when an OTLP endpoint is
 * injected (by the Aspire AppHost in local dev). Likewise, this initialises only when
 * `VITE_OTEL_EXPORTER_OTLP_ENDPOINT` is present — which the AppHost's PortalVueProjectBuilder
 * injects pointing at the Aspire dashboard's OTLP/HTTP receiver. In a normal prod build the
 * var is absent and `initOtel()` is a no-op, so no SDK starts and nothing is exported.
 *
 * What it gives you in the Aspire dashboard:
 *  - Browser spans (document load + every fetch) under service "portal-vue".
 *  - Trace continuity: fetch spans inject W3C traceparent into same-origin/gateway calls,
 *    so a browser action links to the integration-api span it triggered.
 *  - `console.*` writes forwarded via OTLP, trace-linked when emitted inside an active span.
 *    Standardise on `console.*` everywhere; the exported `logger` is only for the rare case
 *    you want structured attributes (not stringified into the message).
 */
import { diag, DiagLogLevel } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

const SERVICE_NAME = import.meta.env.VITE_OTEL_SERVICE_NAME ?? 'portal-vue';
// Base OTLP/HTTP endpoint (no signal suffix), e.g. http://localhost:18890
const ENDPOINT = import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT as string | undefined;
// Optional headers, "key1=val1,key2=val2" — e.g. the dashboard's x-otlp-api-key when secured.
const RAW_HEADERS = import.meta.env.VITE_OTEL_EXPORTER_OTLP_HEADERS as string | undefined;
// 'warn' (default) captures console.warn + console.error; 'all' also captures log/info/debug.
const CONSOLE_LEVEL = (import.meta.env.VITE_OTEL_CAPTURE_CONSOLE_LEVEL ?? 'warn').toLowerCase();

// Original console methods, captured before any patching. Used by the diag logger so OTel's own
// diagnostics never re-enter the capture patch (would loop on export error).
const originalConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
};

function parseHeaders(raw: string | undefined): Record<string, string> {
    if (!raw) return {};
    return Object.fromEntries(
        raw
            .split(',')
            .map((pair) => pair.split('='))
            .filter((kv) => kv.length === 2)
            .map(([k, v]) => [k.trim(), v.trim()]),
    );
}

let initialised = false;

export function initOtel(): void {
    if (initialised || !ENDPOINT) return; // no-op when no endpoint is injected (prod / standalone)
    initialised = true;

    diag.setLogger(
        { error: originalConsole.error, warn: originalConsole.warn, info: () => {}, debug: () => {}, verbose: () => {} },
        DiagLogLevel.WARN,
    );

    const resource = resourceFromAttributes({ [ATTR_SERVICE_NAME]: SERVICE_NAME });
    const headers = parseHeaders(RAW_HEADERS);
    const base = ENDPOINT.replace(/\/$/, '');

    // ---- Traces ----
    const tracerProvider = new WebTracerProvider({
        resource,
        spanProcessors: [new BatchSpanProcessor(new OTLPTraceExporter({ url: `${base}/v1/traces`, headers }))],
    });
    tracerProvider.register({ contextManager: new ZoneContextManager() });

    registerInstrumentations({
        tracerProvider,
        instrumentations: [
            new DocumentLoadInstrumentation(),
            new FetchInstrumentation({
                // Inject W3C traceparent into our own API calls so frontend spans link to backend
                // spans. Matches the gateway-relative API hosts the app calls. Covers the SSE
                // stream (@microsoft/fetch-event-source uses fetch).
                propagateTraceHeaderCorsUrls: [/.*/],
                clearTimingResources: true,
            }),
            // axios (used by all the regular API calls) goes over XMLHttpRequest, NOT fetch — without
            // this, those calls get no browser span and no traceparent, so their backend traces start
            // parentless (e.g. the POST /incidents create had no portal-vue span). This links them.
            new XMLHttpRequestInstrumentation({
                propagateTraceHeaderCorsUrls: [/.*/],
            }),
        ],
    });

    // ---- Logs ----
    const loggerProvider = new LoggerProvider({
        resource,
        processors: [new BatchLogRecordProcessor({ exporter: new OTLPLogExporter({ url: `${base}/v1/logs`, headers }) })],
    });
    logs.setGlobalLoggerProvider(loggerProvider);

    installConsoleCapture();
}

// Severity (text + OTel severity number) per console method.
const CONSOLE_SEVERITY = {
    log: ['INFO', 9],
    info: ['INFO', 9],
    debug: ['DEBUG', 5],
    warn: ['WARN', 13],
    error: ['ERROR', 17],
} as const;

type ConsoleMethod = keyof typeof CONSOLE_SEVERITY;

function methodsForLevel(level: string): ConsoleMethod[] {
    return level === 'all' ? ['log', 'info', 'debug', 'warn', 'error'] : ['warn', 'error'];
}

let capturing = false; // re-entrancy guard: emit() → exporter error → console.error → emit → …

function serializeArg(arg: unknown): string {
    if (typeof arg === 'string') return arg;
    if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
    try {
        return JSON.stringify(arg);
    } catch {
        return String(arg);
    }
}

/**
 * Tee `console.*` into the OTel logs pipeline. Always calls the method it replaces first (devtools
 * unaffected), then emits a log record. The logs SDK stamps trace_id/span_id from the active
 * context, so writes inside a span are trace-linked. Returns a function that puts the replaced
 * methods back. Exported for unit testing.
 */
export function installConsoleCapture(level: string = CONSOLE_LEVEL): () => void {
    const otelLogger = logs.getLogger(SERVICE_NAME);

    const replaced = methodsForLevel(level).map((method) => {
        const [severityText, severityNumber] = CONSOLE_SEVERITY[method];
        const original = console[method];
        console[method] = (...args: unknown[]) => {
            original.apply(console, args);
            if (capturing) return; // don't re-enter from our own / exporter's console writes
            capturing = true;
            try {
                otelLogger.emit({
                    severityText,
                    severityNumber,
                    body: args.map(serializeArg).join(' '),
                });
            } catch {
                // never let logging break the app
            } finally {
                capturing = false;
            }
        };
        return [method, original] as const;
    });

    return () => {
        for (const [method, original] of replaced) console[method] = original;
    };
}

/** App-level logger that forwards to OTLP when initialised; a no-op otherwise. */
export const logger = {
    info: (body: string, attributes?: Record<string, unknown>) => emit('INFO', 9, body, attributes),
    warn: (body: string, attributes?: Record<string, unknown>) => emit('WARN', 13, body, attributes),
    error: (body: string, attributes?: Record<string, unknown>) => emit('ERROR', 17, body, attributes),
};

function emit(severityText: string, severityNumber: number, body: string, attributes?: Record<string, unknown>) {
    if (!initialised) return;
    logs.getLogger(SERVICE_NAME).emit({
        severityText,
        severityNumber,
        body,
        attributes: attributes as Record<string, string | number | boolean> | undefined,
    });
}
